import { useEffect, useRef, useState } from 'react'
import { clampTopPx } from './splitView.js'

/**
 * SplitPane — the right-hand pane of the vertical (side-by-side) split-screen outline (CT-024).
 *
 * Mounted once in OutlineView. It listens for the `open-split-pane` event (dispatched by the
 * Alt+click handler in outline.js) and renders a second, independent outline in a same-origin
 * iframe rooted at the clicked node (the event carries its `?z=…&split=1` deep link). A draggable
 * vertical divider resizes the two panes; the left pane is the existing app (we add right padding
 * to the body so its content still lays out clear of the fixed right pane).
 *
 * Independence is free: the iframe is its own document, so scroll / expand-collapse / navigation
 * never touch the parent, and `split=1` makes the embedded copy skip the shared outline snapshot.
 *
 * Only one split at a time — Alt+clicking while open shows a concise inline error in the divider
 * header (the app's inline-error convention: small #c0392b text) instead of opening another.
 */

const DIVIDER_W = 16       // px — slim vertical divider; doubles as drag handle + hosts the close control
const MIN_PANE = 280       // px — neither pane may collapse below this width
const ERR_MSG = 'Close the current split screen before opening another.'

export default function SplitPane() {
  const [open, setOpen] = useState(false)
  const [src, setSrc] = useState('')
  const [title, setTitle] = useState('')
  const [error, setError] = useState(null)
  const [rightPx, setRightPx] = useState(0)
  const [dragging, setDragging] = useState(false)

  const rightRef = useRef(0)
  const openRef = useRef(false)
  const errTimer = useRef(null)
  rightRef.current = rightPx
  openRef.current = open

  // Open requests come from the engine's Alt+click handler. If a split is already open, surface the
  // inline guard message instead of opening a second one (and leave the existing layout untouched).
  useEffect(() => {
    function onOpen(e) {
      if (openRef.current) {
        setError(ERR_MSG)
        clearTimeout(errTimer.current)
        errTimer.current = setTimeout(() => setError(null), 5000)
        return
      }
      const d = (e && e.detail) || {}
      setSrc(d.url || '/')
      setTitle(d.title || 'Split view')
      setError(null)
      setRightPx(clampTopPx(Math.round(window.innerWidth * 0.45), window.innerWidth, MIN_PANE, DIVIDER_W))
      setOpen(true)
    }
    window.addEventListener('open-split-pane', onOpen)
    return () => window.removeEventListener('open-split-pane', onOpen)
  }, [])

  // While the split is open: tag the body and re-clamp on viewport resize so a shrinking window
  // never collapses a pane. (Listener set up once per open, not per drag frame.)
  useEffect(() => {
    if (!open) return
    document.body.classList.add('aj-split-open')
    function onResize() {
      setRightPx(r => clampTopPx(r, window.innerWidth, MIN_PANE, DIVIDER_W))
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      document.body.classList.remove('aj-split-open')
      document.body.style.paddingRight = ''
      document.body.style.removeProperty('--aj-split-right')
    }
  }, [open])

  // Pad the body so the left pane's content lays out clear of the fixed right pane, and publish
  // --aj-split-right (right pane + divider) so right-anchored chrome (the command bar) ends at the
  // divider instead of running under the split. Both track the divider as it moves.
  useEffect(() => {
    if (!open) return
    document.body.style.paddingRight = (rightPx + DIVIDER_W) + 'px'
    document.body.style.setProperty('--aj-split-right', (rightPx + DIVIDER_W) + 'px')
  }, [open, rightPx])

  function close() {
    clearTimeout(errTimer.current)
    setOpen(false)
    setError(null)          // the guard message is dismissed with the split, never left in single-pane mode
    setSrc('')
  }

  // Divider drag — matches the app's ResizeHandle pattern (document-level pointer listeners, clamp,
  // cursor + userSelect lock). Dragging the divider left grows the right pane. The iframe gets
  // pointer-events:none mid-drag so it doesn't swallow the pointermove stream.
  function onDividerDown(e) {
    e.preventDefault()
    const startX = e.clientX
    const startRight = rightRef.current
    setDragging(true)
    function move(ev) {
      setRightPx(clampTopPx(startRight - (ev.clientX - startX), window.innerWidth, MIN_PANE, DIVIDER_W))
    }
    function up() {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setDragging(false)
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  if (!open) return null

  const rightW = rightPx

  return (
    <>
      <div
        className="aj-split-divider"
        onPointerDown={onDividerDown}
        title="Drag to resize"
        style={{ top: 0, bottom: 0, right: rightW, width: DIVIDER_W, zIndex: 480 }}
      >
        <div className="aj-split-divider-grip" />
        <button
          type="button"
          className="aj-split-close"
          onPointerDown={e => e.stopPropagation()}
          onClick={close}
          title="Close split screen"
          aria-label="Close split screen"
        >✕</button>
      </div>

      {error && (
        <div
          className="aj-split-err"
          style={{ left: Math.max(0, (window.innerWidth - rightW - DIVIDER_W) / 2), top: 12, zIndex: 482 }}
        >{error}</div>
      )}

      <iframe
        className="aj-split-pane aj-split-frame"
        src={src}
        title={title || 'Split outline'}
        style={{ top: 0, bottom: 0, right: 0, width: rightW, zIndex: 479, pointerEvents: dragging ? 'none' : 'auto' }}
      />
    </>
  )
}
